import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Select from "react-select";
import NewEngagementModal from "../Engagements/NewEngagementModal";
import { canContributeToReview } from "../InspectionReviews/reviewPermissions";
import { proposeMemberAssignments } from "./engagementPartnerAssignment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EngagementSelection from "./EngagementSelection.jsx";
import { engagementValue } from "./engagementSelectionData";
import { getData } from "../../helpers/url-request";
import InspectionCycleDetailsForm from "./InspectionCycleDetailsForm";
import UnsavedChangesDialog from "../../components/Common/UnsavedChangesDialog";
import { useContextualHelp } from "../../components/Help/ContextualHelp";
import { startGuidedTour } from "../../components/Help/GuidedTour";
import InspectionCycleBreadcrumbs from "./InspectionCycleBreadcrumbs";
import {
  clearCycleSetupPending,
  markCycleSetupPending,
} from "./inspectionCycleSetupState";
import {
  attachEngagementsToCycle,
  createCycleEngagementReview,
  getCycleEngagementConfiguration,
  getCycleEngagements,
  getInspectionList,
  getInspectionCycle,
  getUnassignedEngagementList,
  getUsersList,
  removeCycleEngagement,
  saveCycleEngagementAssignments,
  saveCycleEngagementChecklists,
} from "../../helpers/backend_helper";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import "./InspectionCycleList.scss";

const wizardSteps = [
  {
    title: "Inspection Details",
    description: "Define the cycle scope and dates.",
  },
  {
    title: "Engagement Selection",
    description: "Select engagements for this cycle.",
  },
  { title: "Team Assignment", description: "Assign an inspection/QM member." },
  {
    title: "Generate & Assign Reviews",
    description: "Confirm configuration and create reviews.",
  },
];

const stepHelp = [
  [
    "Inspection Details",
    "Enter the cycle title, standards, review type and dates. Saving creates a draft cycle so you can complete the remaining setup.",
  ],
  [
    "Engagement Selection",
    "Select engagements for this cycle. You can change the selection before continuing.",
  ],
  [
    "Team Assignment",
    "Choose the inspection/QM member responsible for scoping questions, performing the review, and resolving queries.",
  ],
  [
    "Generate & Assign Reviews",
    "Confirm selected checklists and inspection/QM assignments, then create one review per engagement.",
  ],
];

const createCycleTourSteps = [
  {
    target: ".inspection-cycle-wizard__steps",
    title: "Follow the setup",
    description:
      "Complete cycle details, engagement selection, team assignments, and review generation in order.",
  },
  {
    target: ".inspection-cycle-create-page__surface",
    title: "Complete this step",
    description:
      "The main panel changes as you progress and preserves completed setup.",
  },
  {
    target: ".inspection-cycle-wizard__actions",
    title: "Move through the wizard",
    description: "Use Back and Continue without losing completed steps.",
    placement: "top",
  },
];

const engagementName = (item) =>
  item?.name || item?.engagement?.name || "Untitled engagement";

const engagementNumber = (item) =>
  item?.engagementNo || item?.engagement?.engagementNo || "—";

const engagementId = (item) =>
  Number(item?.engagementId || item?.engagement?.id || item?.id);

const clientName = (item) =>
  item?.client?.clientName ||
  item?.client?.name ||
  item?.engagement?.client?.clientName ||
  item?.engagement?.client?.name ||
  item?.clientName ||
  item?.firmName ||
  item?.engagement?.clientName ||
  item?.engagement?.firmName ||
  "No client";

const detailName = (value) =>
  typeof value === "string" ? value : value?.name || value?.description || "—";

const hasGeneratedReview = (item) =>
  Boolean(
    item?.hasGeneratedReview ||
      item?.reviewId ||
      item?.inspectionId ||
      item?.inspection?.id
  );

const mergeConfigurationDetails = (configuration, cycleLink) => {
  const configurationLink = configuration?.link || {};
  return {
    ...configuration,
    link: {
      ...cycleLink,
      ...configurationLink,
      hasGeneratedReview:
        hasGeneratedReview(configurationLink) || hasGeneratedReview(cycleLink),
      client: configurationLink.client || cycleLink.client,
      workType: configurationLink.workType || cycleLink.workType,
      industryType: configurationLink.industryType || cycleLink.industryType,
      engagement: {
        ...cycleLink.engagement,
        ...configurationLink.engagement,
        client:
          configurationLink.engagement?.client || cycleLink.engagement?.client,
        workType:
          configurationLink.engagement?.workType ||
          cycleLink.engagement?.workType,
        industryType:
          configurationLink.engagement?.industryType ||
          cycleLink.engagement?.industryType,
      },
    },
  };
};

export default function InspectionCycleCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cycleId: routeCycleId } = useParams();
  const { openHelp } = useContextualHelp();
  const [step, setStep] = useState(() =>
    new URLSearchParams(location.search).get("step") === "engagements" ? 1 : 0
  );
  const [cycle, setCycle] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [availableEngagements, setAvailableEngagements] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedEngagementIds, setSelectedEngagementIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviewers, setReviewers] = useState({});
  const [assignmentReasons, setAssignmentReasons] = useState({});
  const reviewerSelections = useRef(reviewers);
  reviewerSelections.current = reviewers;
  const [configurations, setConfigurations] = useState([]);
  const [progress, setProgress] = useState("");
  const [newEngagementOpen, setNewEngagementOpen] = useState(false);
  const [createdEngagementId, setCreatedEngagementId] = useState(null);

  const editCycleId = Number(routeCycleId || 0);
  const isEditMode = editCycleId > 0;
  const cycleId = Number(cycle?.id || cycle?.data?.id || editCycleId || 0);
  const cycleTitle =
    cycle?.title ||
    cycle?.projectTitle ||
    cycle?.data?.title ||
    (isEditMode ? "Inspection Cycle" : "New Inspection Cycle");
  const cancelTarget =
    location.state?.returnTo ||
    (isEditMode ? `/inspections/${editCycleId}/preview` : "/inspections/list");

  const leaveFor = (target, state) => {
    if (leaving || saving) return;
    setLeaving(true);
    window.setTimeout(
      () => navigate(target, state ? { state } : undefined),
      240
    );
  };

  const requestCancel = () =>
    dirty || (!isEditMode && cycleId && step > 0)
      ? setDiscardOpen(true)
      : leaveFor(cancelTarget, { inspectionSlide: "back" });

  useEffect(() => {
    if (!isEditMode) return undefined;
    let current = true;
    setLoading(true);
    getInspectionCycle(editCycleId)
      .then((response) => {
        if (!current) return;
        if (!response?.status || !response.data)
          throw new Error(
            response?.message || "Unable to load the inspection cycle."
          );
        setCycle(response.data);
      })
      .catch((reason) => {
        if (current)
          setError(reason.message || "Unable to load the inspection cycle.");
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [editCycleId, isEditMode]);

  const loadEngagementStep = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    setError("");
    try {
      const [availableResponse, linksResponse, inspectionsResponse] =
        await Promise.all([
          getUnassignedEngagementList(),
          getCycleEngagements(cycleId),
          isEditMode
            ? getInspectionList().catch(() => ({ status: false, data: [] }))
            : Promise.resolve({ status: true, data: [] }),
        ]);
      if (!availableResponse?.status)
        throw new Error(
          availableResponse?.message || "Unable to load engagements."
        );
      if (!linksResponse?.status)
        throw new Error(
          linksResponse?.message || "Unable to load cycle engagements."
        );
      const generatedReviews =
        inspectionsResponse?.status && Array.isArray(inspectionsResponse.data)
          ? inspectionsResponse.data
          : [];
      const nextLinks = (linksResponse.data || []).map((link) => ({
        ...link,
        hasGeneratedReview:
          hasGeneratedReview(link) ||
          generatedReviews.some(
            (review) =>
              Number(review.inspectionCycleEngagementId) === Number(link.id) ||
              (Number(review.inspectionCycleId) === Number(cycleId) &&
                Number(review.engagementId) === engagementId(link))
          ),
      }));
      setLinks(nextLinks);
      setAvailableEngagements(availableResponse.data || []);
      setSelectedEngagementIds((current) =>
        current.length ? current : nextLinks.map(engagementId)
      );
    } catch (reason) {
      setError(reason.message || "Unable to load engagements.");
    } finally {
      setLoading(false);
    }
  }, [cycleId, isEditMode]);

  const loadTeamStep = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    setError("");
    try {
      const [linksResponse, usersResponse, contextResponse] = await Promise.all(
        [
          getCycleEngagements(cycleId),
          getUsersList(),
          getData(
            `/inspection-cycle-engagements/cycle/${cycleId}/assignment-context`
          ),
        ]
      );
      if (!linksResponse?.status)
        throw new Error(
          linksResponse?.message || "Unable to load cycle engagements."
        );
      if (!usersResponse?.status)
        throw new Error(
          usersResponse?.message || "Unable to load team members."
        );
      const nextLinks = linksResponse.data || [];
      setLinks(nextLinks);
      const activeUsers = (usersResponse.data || []).filter(
        (user) =>
          Number(user.status ?? 1) === 1 &&
          Number(user.isBlocked || 0) !== 1 &&
          canContributeToReview(user)
      );
      setUsers(activeUsers);
      if (!contextResponse?.status) {
        throw new Error(
          "Unable to load review workload. Assign members manually or retry."
        );
      }
      const proposal = proposeMemberAssignments(
        nextLinks,
        activeUsers,
        contextResponse.data || [],
        reviewerSelections.current
      );
      setReviewers((current) => ({ ...proposal.assignments, ...current }));
      setAssignmentReasons(proposal.reasons);
    } catch (reason) {
      setError(reason.message || "Unable to load team assignments.");
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  const loadReviewStep = useCallback(async () => {
    if (!links.length) return;
    setLoading(true);
    setError("");
    try {
      const responses = await Promise.all(
        links.map((link) => getCycleEngagementConfiguration(link.id))
      );
      const failed = responses.find((response) => !response?.status);
      if (failed)
        throw new Error(
          failed.message || "Unable to load review configuration."
        );
      const configured = await Promise.all(responses.map(async (response, index) => {
        const configuration = mergeConfigurationDetails(response.data, links[index]);
        const matching = configuration.eligibleChecklists || [];
        if (!hasGeneratedReview(links[index]) && configuration.link.checklists?.length !== 1 && matching.length) {
          const selectedId = configuration.link.checklists?.find((selection) => matching.some((checklist) => checklist.id === selection.checklistId))?.checklistId;
          const saved = await saveCycleEngagementChecklists(configuration.link.id, [selectedId || matching[0].id]);
          if (!saved?.status) throw new Error(saved?.message || "Unable to select matching checklists.");
          const refreshed = await getCycleEngagementConfiguration(configuration.link.id);
          if (!refreshed?.status) throw new Error(refreshed?.message || "Unable to refresh checklist selection.");
          return mergeConfigurationDetails(refreshed.data, links[index]);
        }
        return configuration;
      }));
      setConfigurations(configured);
    } catch (reason) {
      setError(reason.message || "Unable to load review configuration.");
    } finally {
      setLoading(false);
    }
  }, [links]);

  useEffect(() => {
    if (step === 1) loadEngagementStep();
  }, [step, loadEngagementStep]);

  useEffect(() => {
    if (step === 2) loadTeamStep();
  }, [step, loadTeamStep]);

  useEffect(() => {
    if (step === 3) loadReviewStep();
  }, [step, loadReviewStep]);

  const finishDetails = (response) => {
    const created = response?.data || response;
    setCycle(created);
    setDirty(false);
    if (!isEditMode && created?.id) markCycleSetupPending(created.id);
    setStep(1);
  };

  const population = useMemo(() => {
    const existing = links.map((link) => ({
      ...link.engagement,
      id: link.engagementId,
      client: link.client || link.engagement?.client,
      workType: link.workType || link.engagement?.workType,
      industryType: link.industryType || link.engagement?.industryType,
      __link: link,
    }));
    const combined = [
      ...new Map(
        [...availableEngagements, ...existing]
          .filter((item) => engagementId(item) > 0)
          .map((item) => [engagementId(item), item])
      ).values(),
    ];
    return combined;
  }, [availableEngagements, links]);

  const lockedEngagementIds = useMemo(
    () =>
      isEditMode ? links.filter(hasGeneratedReview).map(engagementId) : [],
    [isEditMode, links]
  );
  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: user.fullName || user.emailId,
      })),
    [users]
  );

  const saveEngagements = async (advance = true) => {
    const retainedIds = [
      ...new Set([...selectedEngagementIds, ...lockedEngagementIds]),
    ];
    if (!retainedIds.length) {
      setError("Select at least one engagement.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const existingIds = links.map(engagementId);
      const removed = links.filter(
        (link) =>
          !retainedIds.includes(engagementId(link)) && !hasGeneratedReview(link)
      );
      const added = retainedIds.filter((id) => !existingIds.includes(id));
      await Promise.all(removed.map((link) => removeCycleEngagement(link.id)));
      if (added.length) {
        const response = await attachEngagementsToCycle(cycleId, added);
        if (!response?.status)
          throw new Error(response?.message || "Unable to attach engagements.");
      }
      const refreshed = await getCycleEngagements(cycleId);
      if (!refreshed?.status || !(refreshed.data || []).length)
        throw new Error(refreshed?.message || "No engagements were attached.");
      setLinks(refreshed.data || []);
      if (advance) setStep(2);
      else SuccessNotify("Selection draft saved.");
    } catch (reason) {
      setError(reason.message || "Unable to save engagement selection.");
      ErrorNotify(reason.message || "Unable to save engagement selection.");
    } finally {
      setSaving(false);
    }
  };

  const continueTeam = () => {
    if (
      links.some((link) => !hasGeneratedReview(link) && !reviewers[link.id])
    ) {
      setError("Assign an inspection/QM member to every new engagement.");
      return;
    }
    setError("");
    setStep(3);
  };

  const changeChecklists = async (linkId, selected) => {
    setSaving(true);
    setError("");
    try {
      const response = await saveCycleEngagementChecklists(
        linkId,
        (selected || []).map((option) => Number(option.value))
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to save checklist selection."
        );
      const refreshed = await getCycleEngagementConfiguration(linkId);
      if (!refreshed?.status)
        throw new Error(
          refreshed?.message || "Unable to refresh review configuration."
        );
      setConfigurations((current) =>
        current.map((item) =>
          item.link.id === linkId
            ? mergeConfigurationDetails(refreshed.data, item.link)
            : item
        )
      );
    } catch (reason) {
      setError(reason.message || "Unable to save checklist selection.");
    } finally {
      setSaving(false);
    }
  };

  const generateReviews = async () => {
    const pendingConfigurations = configurations.filter(
      (item) => !hasGeneratedReview(item.link)
    );
    const invalid = pendingConfigurations.find(
      (item) => !item.link?.checklists?.length || !reviewers[item.link.id]
    );
    if (invalid) {
      setError(
        "Every engagement needs at least one checklist and an inspection/QM member before reviews can be generated."
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      for (let index = 0; index < pendingConfigurations.length; index += 1) {
        const configuration = pendingConfigurations[index];
        const link = configuration.link;
        setProgress(
          `Creating review ${index + 1} of ${
            pendingConfigurations.length
          }: ${engagementName(link)}`
        );
        const assignments = link.checklists.flatMap(
          (checklist, checklistIndex) => [
            {
              inspectionCycleChecklistId: checklist.id,
              assignmentType: "PrimaryReviewer",
              roleCode: "Primary Reviewer",
              userId: Number(reviewers[link.id]),
              sortOrder: checklistIndex * 2 + 1,
              isRequired: true,
            },
          ]
        );
        const assignmentResponse = await saveCycleEngagementAssignments(
          link.id,
          assignments
        );
        if (!assignmentResponse?.status)
          throw new Error(
            assignmentResponse?.message ||
              `Unable to assign the team for ${engagementName(link)}.`
          );
        const reviewResponse = await createCycleEngagementReview(link.id);
        if (!reviewResponse?.status)
          throw new Error(
            reviewResponse?.message ||
              `Unable to create the review for ${engagementName(link)}.`
          );
      }
      clearCycleSetupPending(cycleId);
      SuccessNotify(
        isEditMode
          ? pendingConfigurations.length > 0
            ? `Inspection cycle updated and ${
                pendingConfigurations.length
              } new review${
                pendingConfigurations.length === 1 ? "" : "s"
              } created.`
            : "Inspection cycle updated. Existing generated reviews were left unchanged."
          : `${configurations.length} review${
              configurations.length === 1 ? "" : "s"
            } created and assigned.`
      );
      leaveFor(`/inspections/${cycleId}/preview`);
    } catch (reason) {
      setError(reason.message || "Unable to generate reviews.");
      ErrorNotify(reason.message || "Unable to generate reviews.");
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  const goToStep = (target) => {
    if (saving || target >= step || (target === 0 && !cycleId)) return;
    setError("");
    setStep(target);
  };

  const [defaultHelpTitle, helpBody] = stepHelp[step];
  const helpTitle =
    isEditMode && step === 3 ? "Update Cycle & Reviews" : defaultHelpTitle;

  if (
    isEditMode &&
    new URLSearchParams(location.search).get("step") !== "engagements"
  ) {
    const returnToCycle = () => leaveFor(`/inspections/${editCycleId}/preview`);
    return (
      <main
        className={(`inspection-cycle-create-page inspection-cycle-details-edit${
          leaving ? " inspection-cycle-create-page--leaving" : ""
        }`) + " piers-page"}
      >
        <InspectionCycleBreadcrumbs
          items={[
            { label: cycleTitle, to: `/inspections/${editCycleId}/preview` },
            { label: "Edit Inspection Cycle" },
          ]}
          onNavigate={requestCancel}
        />
        <section className="inspection-cycle-create-page__surface inspection-cycle-create-page__surface--details">
          {loading ? (
            <p className="inspection-cycle-wizard__empty">
              Loading inspection cycle...
            </p>
          ) : error ? (
            <p role="alert" className="inspection-cycle-wizard__error">
              {error}
            </p>
          ) : (
            <InspectionCycleDetailsForm
              dataId={editCycleId}
              hideIntro
              submitLabel="Save Changes"
              onDirtyChange={setDirty}
              onRequestClose={requestCancel}
              closeAction={() => {
                setDirty(false);
                returnToCycle();
              }}
            />
          )}
        </section>
        <UnsavedChangesDialog
          isOpen={discardOpen}
          onContinue={() => setDiscardOpen(false)}
          onDiscard={() => {
            setDiscardOpen(false);
            returnToCycle();
          }}
          subject="inspection cycle details"
        />
      </main>
    );
  }

  return (
    <main
      className={(`inspection-cycle-create-page inspection-cycle-create-page--wizard${
        leaving ? " inspection-cycle-create-page--leaving" : ""
      }`) + " piers-page"}
    >
      <InspectionCycleBreadcrumbs
        items={[
          { label: "Inspection Cycles", to: "/inspections/list" },
          { label: isEditMode ? cycleTitle : "Create New Inspection Cycle" },
        ]}
        onNavigate={requestCancel}
      />
      <header className="inspection-cycle-create-page__header piers-page-header">
        <h1 className="piers-page-title">{isEditMode ? "Edit Inspection Cycle" : "New Inspection Cycle"}</h1>
        <p>
          {isEditMode
            ? "Update the inspection cycle details, engagements, team, and generated reviews."
            : "Set up the inspection cycle, select engagements, assign the team, and generate reviews."}
        </p>
      </header>
      <nav
        className="inspection-cycle-wizard__steps"
        aria-label="Inspection cycle setup"
      >
        {wizardSteps.map((item, index) => (
          <button
            type="button"
            key={item.title}
            className={`${index === step ? "is-current" : ""}${
              index < step ? " is-complete" : ""
            }`}
            disabled={
              saving || index > step || (index === 0 && !cycleId && step > 0)
            }
            onClick={() => goToStep(index)}
          >
            <i>{index < step ? <span className="bx bx-check" /> : index + 1}</i>
            <span>
              <strong>
                {isEditMode && index === 4
                  ? "Update Cycle & Reviews"
                  : item.title}
              </strong>
              <small>
                {isEditMode && index === 4
                  ? "Confirm and apply cycle and review updates."
                  : item.description}
              </small>
            </span>
          </button>
        ))}
      </nav>
      <div className="inspection-cycle-create-page__layout">
        <div className="inspection-cycle-create-page__main">
          <section
            className={`inspection-cycle-create-page__surface${
              step === 0
                ? " inspection-cycle-create-page__surface--details"
                : ""
            }${
              step === 1
                ? " inspection-cycle-create-page__surface--engagements"
                : ""
            }${
              step === 2 ? " inspection-cycle-create-page__surface--team" : ""
            }${
              step === 3
                ? " inspection-cycle-create-page__surface--reviews"
                : ""
            }`}
            aria-label={wizardSteps[step].title}
            data-onboarding-target="inspection"
          >
            {step === 0 && (
              <InspectionCycleDetailsForm
                dataId={cycleId}
                hideIntro
                submitLabel={
                  isEditMode ? "Update Cycle & Continue" : "Save & Continue"
                }
                onDirtyChange={setDirty}
                onRequestClose={requestCancel}
                closeAction={finishDetails}
                onCreated={finishDetails}
              />
            )}
            {step === 1 && (
              <EngagementSelection
                onCreate={() => setNewEngagementOpen(true)}
                createdEngagementId={createdEngagementId}
                population={population}
                selectedIds={selectedEngagementIds}
                lockedIds={lockedEngagementIds}
                onChange={setSelectedEngagementIds}
                onBack={() => setStep(0)}
                onSave={() => saveEngagements()}
                onDraft={() => saveEngagements(false)}
                busy={saving}
                loading={loading}
              />
            )}
            {step === 2 && (
              <section className="inspection-cycle-wizard__panel inspection-cycle-wizard__panel--team">
                <header>
                  <div>
                    <h2>Team Assignment</h2>
                    <p>
                      Assign an inspection/QM member for each review. They will
                      select their team during the review.
                    </p>
                  </div>
                  <b>
                    {links.length} engagement{links.length === 1 ? "" : "s"}
                  </b>
                </header>
                {loading ? (
                  <div className="inspection-cycle-wizard__empty">
                    Loading team members…
                  </div>
                ) : (
                  <div className="inspection-cycle-wizard__assignments">
                    {links.map((link) => {
                      const locked = hasGeneratedReview(link);
                      return (
                        <article
                          key={link.id}
                          className={locked ? "is-locked" : ""}
                        >
                          <div className="inspection-cycle-wizard__assignment-identity">
                            <strong>{engagementName(link)}</strong>
                            <small>{clientName(link)}</small>
                            <dl>
                              <div>
                                <dt>Work Type</dt>
                                <dd>
                                  {detailName(
                                    link.workType || link.engagement?.workType
                                  )}
                                </dd>
                              </div>
                              <div>
                                <dt>Industry Type</dt>
                                <dd>
                                  {detailName(
                                    link.industryType ||
                                      link.industry ||
                                      link.engagement?.industryType ||
                                      link.engagement?.industry
                                  )}
                                </dd>
                              </div>
                              <div>
                                <dt>Engagement ID</dt>
                                <dd>{engagementNumber(link)}</dd>
                              </div>
                            </dl>
                            {locked && (
                              <em className="inspection-cycle-wizard__locked-note">
                                <i className="bx bx-lock-alt" /> Generated
                                review — manage reassignment from Inspection
                                Reviews.
                              </em>
                            )}
                          </div>
                          <div className="inspection-cycle-wizard__user-fields">
                            <label>
                              Inspection / QM Member
                              {assignmentReasons[link.id] && (
                                <small>
                                  Automatically selected:{" "}
                                  {assignmentReasons[link.id]}
                                </small>
                              )}
                              <Select
                                className="inspection-cycle-wizard__user-select"
                                classNamePrefix="inspection-cycle-wizard-select"
                                inputId={`primary-reviewer-${link.id}`}
                                isSearchable
                                isDisabled={locked}
                                options={userOptions}
                                placeholder="Search and select inspection/QM member"
                                noOptionsMessage={() => "No team members found"}
                                value={
                                  userOptions.find(
                                    (option) =>
                                      Number(option.value) ===
                                      Number(reviewers[link.id])
                                  ) || null
                                }
                                onChange={(option) => {
                                  setReviewers((current) => ({
                                    ...current,
                                    [link.id]: option?.value || "",
                                  }));
                                  setAssignmentReasons((current) => ({
                                    ...current,
                                    [link.id]: "",
                                  }));
                                }}
                              />
                            </label>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
                <WizardActions
                  back={() => setStep(1)}
                  next={continueTeam}
                  nextLabel="Continue"
                  disabled={saving || loading}
                />
              </section>
            )}
            {step === 3 && (
              <section className="inspection-cycle-wizard__panel inspection-cycle-wizard__panel--reviews">
                <header>
                  <div>
                    <h2>
                      {isEditMode
                        ? "Update Cycle & Reviews"
                        : "Generate & Assign Reviews"}
                    </h2>
                    <p>
                      {isEditMode
                        ? "Confirm the selected checklists and inspection/QM members before applying updates."
                        : "Confirm selected checklists and inspection/QM members before creating reviews."}
                    </p>
                  </div>
                  <b>
                    {isEditMode ? "Update" : "Create"} {configurations.length}{" "}
                    Inspection Review
                    {configurations.length === 1 ? "" : "s"}
                  </b>
                </header>
                {loading ? (
                  <div className="inspection-cycle-wizard__empty">
                    Loading review configuration…
                  </div>
                ) : (
                  <div className="inspection-cycle-wizard__reviews">
                    {configurations.map((configuration) => {
                      const link = configuration.link;
                      const locked = hasGeneratedReview(link);
                      const reviewer = users.find(
                        (user) => Number(user.id) === Number(reviewers[link.id])
                      );
                      const options = (
                        configuration.eligibleChecklists || []
                      ).map((checklist) => ({
                        value: checklist.id,
                        label: checklist.name,
                      }));
                      const selectedChecklists = (link.checklists || []).map(
                        (selection) =>
                          options.find(
                            (option) =>
                              Number(option.value) ===
                              Number(selection.checklistId)
                          ) || {
                            value: selection.checklistId,
                            label:
                              selection.checklist?.name ||
                              `Checklist ${selection.checklistId}`,
                          }
                      );
                      return (
                        <article
                          key={link.id}
                          className={locked ? "is-locked" : ""}
                        >
                          <div className="inspection-cycle-wizard__review-title">
                            <span
                              className={`bx ${
                                link.checklists?.length
                                  ? "bx-check-circle"
                                  : "bx-error-circle"
                              }`}
                            />
                            <div>
                              <strong>{engagementName(link)}</strong>
                              <small>{clientName(link)}</small>
                              <dl>
                                <div>
                                  <dt>Work Type</dt>
                                  <dd>
                                    {detailName(
                                      link.workType || link.engagement?.workType
                                    )}
                                  </dd>
                                </div>
                                <div>
                                  <dt>Industry Type</dt>
                                  <dd>
                                    {detailName(
                                      link.industryType ||
                                        link.industry ||
                                        link.engagement?.industryType ||
                                        link.engagement?.industry
                                    )}
                                  </dd>
                                </div>
                                <div>
                                  <dt>Engagement ID</dt>
                                  <dd>{engagementNumber(link)}</dd>
                                </div>
                              </dl>
                              {locked && (
                                <em className="inspection-cycle-wizard__locked-note">
                                  <i className="bx bx-lock-alt" /> Review
                                  already generated
                                </em>
                              )}
                            </div>
                          </div>
                          <label>
                            Checklist
                            <Select
                              className="inspection-cycle-wizard__template-select"
                              classNamePrefix="inspection-cycle-wizard-select"
                              inputId={`review-checklists-${link.id}`}
                              isSearchable
                              isClearable={false}
                              isDisabled={saving || locked}
                              options={options}
                              value={selectedChecklists[0] || null}
                              placeholder="Search and select a checklist"
                              noOptionsMessage={() =>
                                "No checklists match the work type, industry, review type and cycle standards"
                              }
                              onChange={(selected) =>
                                changeChecklists(link.id, selected ? [selected] : [])
                              }
                            />
                          </label>
                          <span>
                            <b>{link.checklists?.length || 0}</b> checklist
                            {link.checklists?.length === 1 ? "" : "s"}
                          </span>
                          <div className="inspection-cycle-wizard__review-assignees">
                            <span>
                              <small>Inspection / QM Member</small>
                              <b>
                                {reviewer?.fullName ||
                                  reviewer?.emailId ||
                                  "Unassigned"}
                              </b>
                            </span>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
                {progress && (
                  <div
                    className="inspection-cycle-wizard__progress"
                    role="status"
                  >
                    <i className="bx bx-loader-alt bx-spin" />
                    {progress}
                  </div>
                )}
                <WizardActions
                  back={() => setStep(2)}
                  next={generateReviews}
                  nextLabel={
                    isEditMode
                      ? saving
                        ? "Updating…"
                        : "Update Cycle & Reviews"
                      : saving
                      ? "Generating…"
                      : `Create ${
                          configurations.length || ""
                        } Inspection Review${
                          configurations.length === 1 ? "" : "s"
                        }`
                  }
                  disabled={saving || loading || !configurations.length}
                />
              </section>
            )}
            {error && (
              <div className="inspection-cycle-wizard__error" role="alert">
                <i className="bx bx-error-circle" />
                {error}
              </div>
            )}
          </section>
        </div>
        <aside
          className="inspection-cycle-create-page__aside"
          aria-label="Help and guided tour"
        >
          {step === 1 && (
            <section className="inspection-cycle-create-page__help engagement-population">
              <h2>Selection summary</h2>
              <p>
                Available engagements <strong>{population.length}</strong>
              </p>
              <p>
                Selected engagements{" "}
                <strong>{selectedEngagementIds.length}</strong>
              </p>
              <p>
                Partners{" "}
                <strong>
                  {
                    new Set(
                      population
                        .map((item) => engagementValue(item, "partner"))
                        .filter(Boolean)
                    ).size
                  }
                </strong>
              </p>
              <p>
                Engagement types{" "}
                <strong>
                  {
                    new Set(
                      population
                        .map((item) => engagementValue(item, "type"))
                        .filter(Boolean)
                    ).size
                  }
                </strong>
              </p>
              <small>
                Active available engagements and engagements already in this
                cycle. Partner and work type filters help narrow the selection.
              </small>
            </section>
          )}
          <section className="inspection-cycle-create-page__help">
            <header>
              <span>
                <i className="bx bx-book-open" />
              </span>
              <div>
                <small>STEP {step + 1} OF 4</small>
                <h2>{helpTitle}</h2>
              </div>
            </header>
            <h3>
              <i className="bx bx-info-circle" /> About this step
            </h3>
            <p>{helpBody}</p>
            <button type="button" onClick={openHelp}>
              Open Help Center <i className="bx bx-chevron-right" />
            </button>
          </section>
          <section className="inspection-cycle-create-page__tour">
            <h2>
              <i className="bx bx-compass" /> Inspection setup guide
            </h2>
            <p>Tour the four steps used to prepare and generate reviews.</p>
            <button
              type="button"
              onClick={() => startGuidedTour(createCycleTourSteps)}
            >
              <i className="bx bx-play" /> Start Guided Tour
            </button>
          </section>
        </aside>
      </div>
      <NewEngagementModal
        isOpen={newEngagementOpen}
        initialInspectionCycleId={cycleId}
        deferCycleAssignment
        onCancel={() => setNewEngagementOpen(false)}
        onSaved={(engagement) => {
          const id = Number(engagement?.id);
          if (!id) return;
          setAvailableEngagements((current) => [
            engagement,
            ...current.filter((item) => engagementId(item) !== id),
          ]);
          setSelectedEngagementIds((current) => [...new Set([...current, id])]);
          setCreatedEngagementId(id);
          setDirty(true);
          setNewEngagementOpen(false);
        }}
      />
      <UnsavedChangesDialog
        isOpen={discardOpen}
        onContinue={() => setDiscardOpen(false)}
        onDiscard={() => {
          setDiscardOpen(false);
          if (!isEditMode && cycleId) clearCycleSetupPending(cycleId);
          leaveFor(cancelTarget, { inspectionSlide: "back" });
        }}
        subject="inspection cycle setup"
      />
    </main>
  );
}

function WizardActions({ back, next, nextLabel, disabled }) {
  return (
    <footer className="inspection-cycle-wizard__actions">
      <button
        type="button"
        className="is-secondary"
        onClick={back}
        disabled={disabled}
      >
        <i className="bx bx-left-arrow-alt" /> Back
      </button>
      <button
        type="button"
        className="is-primary"
        onClick={next}
        disabled={disabled}
      >
        {nextLabel}
        <i className="bx bx-right-arrow-alt" />
      </button>
    </footer>
  );
}
